import { theme } from "@classdojo/web/nessie";
import { NessieThemeColors } from "@classdojo/web/nessie/components/theme";
import { RAW_cssValue } from "@classdojo/web/nessie/stylingLib";
import { TranslatedString } from "@classdojo/web/pods/i18n/translate";
import React from "react";
import FixedHeaderScroller from "./FixedHeaderScroller";
import { IdeasChevronLeftIcon } from "components/IdeasIcons";
import { closeWebview } from "src/pods/webviewInit";

const hexRegex = /^#[0-9a-fA-F]{6}$/;

type ToolkitAppScrollerProps = {
  title: TranslatedString;
  color: NessieThemeColors | string;
  imgSrc: string;
  children: React.ReactNode;
};

const isThemeColor = (color: NessieThemeColors | string): color is NessieThemeColors => {
  return color in theme.colors;
};

export default function ToolkitAppScroller({ title, color, imgSrc, children }: ToolkitAppScrollerProps) {
  const actualColor = isThemeColor(color) && theme.colors[color] ? theme.colors[color] : color;
  const backgroundImageForOverscroll = hexRegex.test(actualColor)
    ? // if we are given a hex color, make it a gradient so it can be used as a background-image
      `linear-gradient(${actualColor} 0%, ${actualColor} 100%)`
    : // otherwise, assume we've been given a background-image already
      actualColor;

  // make it so the background-image is rendered for the top half of the box
  const backgroundForOverscroll = `top left / 100% 50% no-repeat ${backgroundImageForOverscroll}`;

  return (
    <FixedHeaderScroller
      headerBackground={actualColor}
      header={
        <div sx={{ textAlign: "center", paddingTop: "dt_s", paddingBottom: "dt_s", position: "relative" }}>
          <div
            sx={{
              position: "absolute",
              top: "dt_m",
              left: "dt_s",
              zIndex: 10,
            }}
            onClick={() => {
              // for iOS native toolkit landing page
              // handling on the iOS side for this action does not exist on older clients
              // so shouldn't affect toolkit with the webview landing page
              closeWebview();
              window.history.back();
            }}
          >
            <IdeasChevronLeftIcon color="dt_content_light" />
          </div>
          <div
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <div sx={{ color: "dt_content_light", fontWeight: 800, fontSize: 30 }}>{title}</div>
          </div>
        </div>
      }
      contentBackground={backgroundForOverscroll}
    >
      <div
        sx={{
          backgroundColor: RAW_cssValue(actualColor),
          background: RAW_cssValue(actualColor),
          textAlign: "center",
          padding: "dt_l",
          paddingTop: "dt_m",
          position: "relative",
        }}
      >
        <img width="64px" src={imgSrc} />
      </div>
      <div
        sx={{
          minHeight: "50%",
          backgroundColor: "dt_background_primary",
          overflow: "hidden" /* prevents margin collapse */,
        }}
      >
        {children}
      </div>
    </FixedHeaderScroller>
  );
}
